import apiClient from '@/services/api/apiClient';

export interface ProviderStatus {
  configured: boolean;
  label: string;
  docsUrl: string;
  features: string[];
  hasPhoneNumber?: boolean;
  hasAgent?: boolean;
}

export interface CallRecord {
  id: string;
  contactId: string;
  journeyId?: string;
  provider: 'vapi' | 'retell';
  callType: string;
  externalCallId?: string;
  status: string;
  phoneNumber?: string;
  duration?: number;
  transcript?: string;
  summary?: string;
  recordingUrl?: string;
  createdAt: string;
  endedAt?: string;
}

export interface LiveCallStatus {
  callId: string;
  provider: string;
  status: string;
  duration: number | null;
  startedAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  transcript: string | null;
  summary: string | null;
  recordingUrl: string | null;
  cost: number | null;
}

const aiCallingService = {
  getProviders: async () => {
    const { data } = await apiClient.get('/api/ai-calling/providers');
    return data as Record<string, ProviderStatus>;
  },

  initiateCall: async (params: {
    contactId: string;
    journeyId?: string;
    provider: 'vapi' | 'retell';
    callType?: 'CALL1' | 'CALL2' | 'CALL3';
    phoneNumber?: string;
    agentNote?: string;
    usePreCallBrief?: boolean;
  }) => {
    const { data } = await apiClient.post('/api/ai-calling/initiate', params, { timeout: 180000 });
    return data as { callId: string; provider: string; status: string; contactName: string; accountName: string; phoneNumber: string; message: string };
  },

  getStatus: async (callId: string, provider: string) => {
    const { data } = await apiClient.get(`/api/ai-calling/status/${callId}?provider=${provider}`);
    return data as LiveCallStatus;
  },

  endCall: async (callId: string, provider: string) => {
    const { data } = await apiClient.post(`/api/ai-calling/end/${callId}`, { provider });
    return data as { callId: string; status: string; message: string };
  },

  getHistory: async (contactId: string) => {
    const { data } = await apiClient.get(`/api/ai-calling/history/${contactId}`);
    return data as { total: number; data: CallRecord[] };
  },

  // Pull the latest transcript/analysis from the provider for this contact
  // (recovers calls whose webhook didn't land), then history reflects it.
  syncContact: async (contactId: string) => {
    const { data } = await apiClient.post(`/api/ai-calling/sync-contact/${contactId}`, {});
    return data as { ok: boolean; transcript?: string | null; status?: string };
  },
};

export default aiCallingService;
